/**
 * Benachrichtigungs-Einstellungen — Kanäle, Kategorien & Ruhezeiten
 * 
 * Erlaubt dem Nutzer, Benachrichtigungskanäle (Telegram, Push, E-Mail),
 * Kategorien (Streak, Programm, Tagebuch, Wochen-Rückblick) mit einstellbarer Uhrzeit
 * und Ruhezeiten (Quiet Hours) zu steuern.
 */
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import {
  Bell,
  BellOff,
  Loader2,
  Send as SendIcon,
  Smartphone,
  Mail,
  Flame,
  BookOpen,
  Calendar,
  BarChart3,
  Moon,
  Clock,
} from "lucide-react";
import { toast } from "sonner";

interface Props {
  clientId: string;
}

export default function NotificationPreferencesSection({ clientId }: Props) {
  const { data: prefs, isLoading } = trpc.notifications.getPreferences.useQuery(
    { clientId },
    { enabled: !!clientId }
  );
  const updateMutation = trpc.notifications.updatePreferences.useMutation();
  const utils = trpc.useUtils();
  const push = usePushNotifications(clientId);
  const [saving, setSaving] = useState(false);

  const handleToggle = async (
    field: string,
    currentValue: boolean
  ) => {
    setSaving(true);
    try {
      await updateMutation.mutateAsync({
        clientId,
        [field]: !currentValue,
      });
      utils.notifications.getPreferences.invalidate({ clientId });
    } catch {
      toast.error("Einstellung konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const handleHourChange = async (field: string, value: number) => {
    setSaving(true);
    try {
      await updateMutation.mutateAsync({
        clientId,
        [field]: value,
      });
      utils.notifications.getPreferences.invalidate({ clientId });
      toast.success("Uhrzeit aktualisiert.");
    } catch {
      toast.error("Einstellung konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const handlePushToggle = async () => {
    if (push.isSubscribed) {
      await push.unsubscribe();
      toast.success("Push-Benachrichtigungen deaktiviert.");
    } else {
      await push.subscribe();
      if (push.permission === "denied") {
        toast.error("Push-Benachrichtigungen sind im Browser blockiert. Bitte erlaube sie in den Browser-Einstellungen.");
      } else {
        toast.success("Push-Benachrichtigungen aktiviert!");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
        <div className="px-5 py-4 border-b border-[#2D2D2D]/5 flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#C9A96E]" />
          <h2 className="text-sm font-medium text-[#2D2D2D]">Benachrichtigungen</h2>
        </div>
        <div className="p-5 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-[#C9A96E]" />
          <span className="text-sm text-[#2D2D2D]/40">Lade Einstellungen...</span>
        </div>
      </div>
    );
  }

  if (!prefs) return null;

  const ToggleSwitch = ({
    enabled,
    onToggle,
    disabled,
  }: {
    enabled: boolean;
    onToggle: () => void;
    disabled?: boolean;
  }) => (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`relative w-9 h-5 rounded-full transition-colors ${
        enabled ? "bg-[#C9A96E]" : "bg-[#2D2D2D]/15"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
          enabled ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );

  // Generate hour options for select
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${i.toString().padStart(2, "0")}:00`,
  }));

  // Time picker inline component
  const TimePicker = ({
    value,
    onChange,
    disabled,
  }: {
    value: number;
    onChange: (v: number) => void;
    disabled?: boolean;
  }) => (
    <div className="flex items-center gap-1.5">
      <Clock className="w-3 h-3 text-[#2D2D2D]/30" />
      <select
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        disabled={disabled}
        className="bg-white border border-[#2D2D2D]/10 rounded-md px-2 py-0.5 text-[10px] text-[#2D2D2D] focus:outline-none focus:ring-1 focus:ring-[#C9A96E] disabled:opacity-40"
      >
        {hourOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
      <div className="px-5 py-4 border-b border-[#2D2D2D]/5 flex items-center gap-2">
        <Bell className="w-4 h-4 text-[#C9A96E]" />
        <h2 className="text-sm font-medium text-[#2D2D2D]">Benachrichtigungen</h2>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-[#C9A96E] ml-auto" />}
      </div>

      <div className="p-5 space-y-5">
        {/* ── Ruhezeiten (Quiet Hours) ── */}
        <div>
          <p className="text-xs font-semibold text-[#2D2D2D]/60 uppercase tracking-wider mb-3">
            Ruhezeiten
          </p>
          <div className="bg-[#F5F0EB] rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                <Moon className="w-4 h-4 text-indigo-500" />
              </div>
              <div>
                <p className="text-xs font-medium text-[#2D2D2D]">Keine Benachrichtigungen von</p>
                <p className="text-[10px] text-[#2D2D2D]/40">In dieser Zeit werden alle Benachrichtigungen blockiert</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <div className="flex-1">
                <label className="text-[10px] text-[#2D2D2D]/50 mb-1 block">Von</label>
                <select
                  value={prefs.quietHoursStart}
                  onChange={(e) => handleHourChange("quietHoursStart", parseInt(e.target.value))}
                  className="w-full bg-white border border-[#2D2D2D]/10 rounded-lg px-3 py-2 text-xs text-[#2D2D2D] focus:outline-none focus:ring-1 focus:ring-[#C9A96E]"
                >
                  {hourOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <span className="text-xs text-[#2D2D2D]/40 pt-4">bis</span>
              <div className="flex-1">
                <label className="text-[10px] text-[#2D2D2D]/50 mb-1 block">Bis</label>
                <select
                  value={prefs.quietHoursEnd}
                  onChange={(e) => handleHourChange("quietHoursEnd", parseInt(e.target.value))}
                  className="w-full bg-white border border-[#2D2D2D]/10 rounded-lg px-3 py-2 text-xs text-[#2D2D2D] focus:outline-none focus:ring-1 focus:ring-[#C9A96E]"
                >
                  {hourOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-[10px] text-[#2D2D2D]/40 mt-2">
              Aktuell: Keine Benachrichtigungen zwischen {prefs.quietHoursStart.toString().padStart(2, "0")}:00 und {prefs.quietHoursEnd.toString().padStart(2, "0")}:00 Uhr
            </p>
          </div>
        </div>

        {/* ── Kanäle ── */}
        <div>
          <p className="text-xs font-semibold text-[#2D2D2D]/60 uppercase tracking-wider mb-3">
            Kanäle
          </p>
          <div className="space-y-2">
            {/* Telegram */}
            <div className="bg-[#F5F0EB] rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#0088cc]/10 flex items-center justify-center">
                  <SendIcon className="w-4 h-4 text-[#0088cc]" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#2D2D2D]">Telegram</p>
                  <p className="text-[10px] text-[#2D2D2D]/40">Höchste Priorität</p>
                </div>
              </div>
              <ToggleSwitch
                enabled={prefs.channelTelegram}
                onToggle={() => handleToggle("channelTelegram", prefs.channelTelegram)}
              />
            </div>

            {/* Push */}
            <div className="bg-[#F5F0EB] rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#C9A96E]/10 flex items-center justify-center">
                  <Smartphone className="w-4 h-4 text-[#C9A96E]" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#2D2D2D]">Push-Benachrichtigungen</p>
                  <p className="text-[10px] text-[#2D2D2D]/40">
                    {push.isSupported
                      ? push.permission === "denied"
                        ? "Im Browser blockiert"
                        : push.isSubscribed
                          ? "Aktiv auf diesem Gerät"
                          : "Nicht aktiviert"
                      : "Nicht unterstützt"}
                  </p>
                </div>
              </div>
              {push.isSupported && (
                <ToggleSwitch
                  enabled={push.isSubscribed && prefs.channelPush}
                  onToggle={async () => {
                    if (!push.isSubscribed) {
                      await handlePushToggle();
                    }
                    handleToggle("channelPush", prefs.channelPush);
                  }}
                  disabled={push.isLoading || push.permission === "denied"}
                />
              )}
            </div>

            {/* E-Mail */}
            <div className="bg-[#F5F0EB] rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#6b7f4e]/10 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#6b7f4e]" />
                </div>
                <div>
                  <p className="text-xs font-medium text-[#2D2D2D]">E-Mail</p>
                  <p className="text-[10px] text-[#2D2D2D]/40">Fallback-Kanal</p>
                </div>
              </div>
              <ToggleSwitch
                enabled={prefs.channelEmail}
                onToggle={() => handleToggle("channelEmail", prefs.channelEmail)}
              />
            </div>
          </div>
        </div>

        {/* ── Kategorien ── */}
        <div>
          <p className="text-xs font-semibold text-[#2D2D2D]/60 uppercase tracking-wider mb-3">
            Kategorien
          </p>
          <div className="space-y-2">
            {/* Streak-Erinnerung */}
            <div className="bg-[#F5F0EB] rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
                    <Flame className="w-4 h-4 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[#2D2D2D]">Streak-Warnung</p>
                    <p className="text-[10px] text-[#2D2D2D]/40">Bei gefährdeter Streak</p>
                  </div>
                </div>
                <ToggleSwitch
                  enabled={prefs.streakReminder}
                  onToggle={() => handleToggle("streakReminder", prefs.streakReminder)}
                />
              </div>
              {prefs.streakReminder && (
                <div className="mt-2 ml-11 flex items-center gap-2">
                  <span className="text-[10px] text-[#2D2D2D]/40">Täglich um</span>
                  <TimePicker
                    value={prefs.streakReminderHour}
                    onChange={(v) => handleHourChange("streakReminderHour", v)}
                  />
                </div>
              )}
            </div>

            {/* Programm-Erinnerung */}
            <div className="bg-[#F5F0EB] rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[#2D2D2D]">Programm-Erinnerung</p>
                    <p className="text-[10px] text-[#2D2D2D]/40">Bei aktivem Programm</p>
                  </div>
                </div>
                <ToggleSwitch
                  enabled={prefs.programReminder}
                  onToggle={() => handleToggle("programReminder", prefs.programReminder)}
                />
              </div>
              {prefs.programReminder && (
                <div className="mt-2 ml-11 flex items-center gap-2">
                  <span className="text-[10px] text-[#2D2D2D]/40">Täglich um</span>
                  <TimePicker
                    value={prefs.programReminderHour}
                    onChange={(v) => handleHourChange("programReminderHour", v)}
                  />
                </div>
              )}
            </div>

            {/* Tagebuch-Erinnerung */}
            <div className="bg-[#F5F0EB] rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[#2D2D2D]">Tagebuch-Erinnerung</p>
                    <p className="text-[10px] text-[#2D2D2D]/40">Tägliche Erinnerung ans Tagebuch</p>
                  </div>
                </div>
                <ToggleSwitch
                  enabled={prefs.journalReminder}
                  onToggle={() => handleToggle("journalReminder", prefs.journalReminder)}
                />
              </div>
              {prefs.journalReminder && (
                <div className="mt-2 ml-11 flex items-center gap-2">
                  <span className="text-[10px] text-[#2D2D2D]/40">Abends um</span>
                  <TimePicker
                    value={prefs.journalReminderHour}
                    onChange={(v) => handleHourChange("journalReminderHour", v)}
                  />
                </div>
              )}
            </div>

            {/* Wochen-Rückblick */}
            <div className="bg-[#F5F0EB] rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <BarChart3 className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[#2D2D2D]">Wochen-Rückblick</p>
                    <p className="text-[10px] text-[#2D2D2D]/40">Sonntags — Zusammenfassung</p>
                  </div>
                </div>
                <ToggleSwitch
                  enabled={prefs.weeklyDigest}
                  onToggle={() => handleToggle("weeklyDigest", prefs.weeklyDigest)}
                />
              </div>
              {prefs.weeklyDigest && (
                <div className="mt-2 ml-11 flex items-center gap-2">
                  <span className="text-[10px] text-[#2D2D2D]/40">Sonntag um</span>
                  <TimePicker
                    value={prefs.weeklyDigestHour}
                    onChange={(v) => handleHourChange("weeklyDigestHour", v)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Info-Hinweis */}
        <div className="bg-[#C9A96E]/5 rounded-xl p-3 flex items-start gap-2">
          <BellOff className="w-3.5 h-3.5 text-[#C9A96E] mt-0.5 shrink-0" />
          <p className="text-[10px] text-[#2D2D2D]/50 leading-relaxed">
            Priorität: Telegram &gt; Push &gt; E-Mail. Die E-Mail wird nur gesendet, wenn weder Telegram noch Push erfolgreich waren. Während der Ruhezeiten werden keine Benachrichtigungen gesendet.
          </p>
        </div>
      </div>
    </div>
  );
}
